import { useEffect, useState } from 'react';
import { FileSpreadsheet, FileDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';

// Admin: download the full BDAF register (every analysed business + every indicator) as XLSX / CSV.
export function RegisterExport() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.get('/admin/export/register/status').then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!status?.running) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [status?.running]);

  const download = async (format) => {
    setBusy(format);
    try {
      const { data, headers } = await api.get('/admin/export/register', { params: { format }, responseType: 'blob' });
      const name = (headers['content-disposition'] || '').match(/filename="?([^";]+)"?/)?.[1] || `adoptic_register.${format}`;
      const url = URL.createObjectURL(data);
      const a = document.createElement('a'); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.response?.status === 404 ? 'No export yet — click Regenerate' : 'Download failed'); } finally { setBusy(''); }
  };
  const regenerate = async () => {
    try { await api.post('/admin/export/register/regenerate'); toast.success('Rebuilding the register export — usually 1–2 minutes'); load(); }
    catch { toast.error('Could not start export'); }
  };
  const f = status?.files || {};
  return (
    <div className="solid-card p-4 flex flex-wrap items-center gap-3" data-testid="register-export">
      <FileSpreadsheet className="w-4 h-4 text-forest" />
      <div className="flex-1 min-w-[220px]">
        <p className="font-display text-sm font-extrabold text-ink">Full BDAF register — every indicator cross-referenced · {status?.businesses?.toLocaleString() ?? '…'} businesses</p>
        <p className="font-body text-xs text-ink/60">
          {f.xlsx ? `Last built ${new Date(f.xlsx.generated_at).toLocaleString()} · XLSX ${(f.xlsx.size_kb / 1024).toFixed(1)} MB · CSV ${((f.csv?.size_kb || 0) / 1024).toFixed(1)} MB` : 'No export built yet'}
          {status?.running && <span className="text-status-amber"> · rebuilding…</span>}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" disabled={!f.xlsx || busy === 'xlsx'} onClick={() => download('xlsx')} className="h-8 rounded-full bg-forest hover:opacity-90 font-body text-xs" data-testid="export-xlsx-btn"><FileDown className="w-3.5 h-3.5 mr-1" /> Excel (.xlsx)</Button>
        <Button size="sm" variant="outline" disabled={!f.csv || busy === 'csv'} onClick={() => download('csv')} className="h-8 rounded-full font-body text-xs" data-testid="export-csv-btn"><FileDown className="w-3.5 h-3.5 mr-1" /> CSV</Button>
        <Button size="sm" variant="ghost" disabled={status?.running} onClick={regenerate} className="h-8 rounded-full font-body text-xs" data-testid="export-regenerate-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${status?.running ? 'animate-spin' : ''}`} /> Regenerate</Button>
      </div>
    </div>
  );
}
